import React, { useEffect, useState } from 'react'
import { Toast as BootstrapToast } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Toast as ToastType } from '../../types'
import { copyToClipboard } from '../../utils/clipboard'
import './PopupToast.scss'

interface PopupToastProps {
  toast: ToastType | null
  autohide?: boolean
  onClose: (toast: ToastType) => void
  onHidden?: (toast: ToastType) => void
}

export const PopupToast: React.FC<PopupToastProps> = ({ toast, autohide = true, onClose, onHidden }) => {
  const { t } = useTranslation()
  const [delayRemaining, setDelayRemaining] = useState(0)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!toast || !autohide || !toast.delay) return

    const refreshInterval = 150
    const delay = (toast.delay || 0) - 500
    const totalSteps = Math.round(delay / refreshInterval)
    let currentStep = 0

    setDelayRemaining(toast.delay)

    const interval = setInterval(() => {
      currentStep++
      const elapsed = refreshInterval * currentStep
      const remaining = Math.max(0, toast.delay - elapsed)
      setDelayRemaining(remaining)

      if (currentStep >= totalSteps) {
        clearInterval(interval)
        setDelayRemaining(0)
      }
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [toast, autohide])


  if (!toast) return null

  const handleClose = () => {
    onClose(toast)
  }

  const handleHidden = () => {
    if (onHidden) {
      onHidden(toast)
    }
  }

  const handleAction = () => {
    if (toast.action) {
      toast.action()
    }
    handleClose()
  }

  const isDetailedError = (error: any): boolean => {
    return typeof error === 'object' && 'status' in error && 'statusText' in error && 'url' in error && 'message' in error && 'error' in error
  }

  const copyError = async (error: any) => {
    await copyToClipboard(JSON.stringify(error))
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 3000)
  }

  const getErrorText = (error: any) => {
    if (typeof error === 'object' && error.error) {
      if (typeof error.error === 'object' && !Array.isArray(error.error)) {
        const errorObj = error.error
        const errorMessages: string[] = []

        Object.keys(errorObj).forEach((key) => {
          const fieldErrors = errorObj[key]
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach((err: string) => {
              const translated = t(err, { defaultValue: err })
              errorMessages.push(`${key}: ${translated}`)
            })
          } else if (typeof fieldErrors === 'string') {
            const translated = t(fieldErrors, { defaultValue: fieldErrors })
            errorMessages.push(`${key}: ${translated}`)
          }
        })

        if (errorMessages.length > 0) {
          return errorMessages.join('\n')
        }

        if (errorObj.detail) {
          return t(errorObj.detail, { defaultValue: errorObj.detail })
        }
      }

      if (typeof error.error === 'string') {
        return t(error.error, { defaultValue: error.error })
      }

      if (Array.isArray(error.error)) {
        return error.error.map((err: string) => t(err, { defaultValue: err })).join('\n')
      }
    }

    let text: string = error?.error?.detail ?? error?.error ?? ''
    if (typeof text === 'object') text = JSON.stringify(text, null, 2)
    const translated = t(text, { defaultValue: text })
    return `${translated.slice(0, 500)}${translated.length > 500 ? '...' : ''}`
  }

  const progressPercentage = toast.delay && toast.delay > 0 ? Math.max(0, (delayRemaining / toast.delay) * 100) : 0
  const effectiveAutohide = autohide && Boolean(toast.delay && toast.delay > 0)

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        zIndex: 9999,
        minWidth: '300px',
        maxWidth: '500px',
      }}
    >
      <BootstrapToast
        show={true}
        autohide={effectiveAutohide}
        delay={toast.delay}
        className={`mb-2 position-relative ${toast.classname || ''}`}
        onClose={handleClose}
        onExited={handleHidden}
      >
        {effectiveAutohide && delayRemaining > 0 && (
          <>
            <div
              className="position-absolute start-0 bottom-0 end-0 pe-none toast-progress-overlay"
              style={{
                left: 0,
                right: 0,
                bottom: 0,
                height: '10px',
                width: '100%',
                zIndex: 1,
              }}
            >
              <div
                className="progress h-100 w-100"
                role="progressbar"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.15)',
                  height: '100%',
                  width: '100%',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  overflow: 'hidden',
                }}
              >
                <div
                  className="progress-bar"
                  style={{
                    width: `${progressPercentage}%`,
                    backgroundColor: toast.error ? 'var(--bs-danger)' : toast.classname === 'success' ? 'var(--bs-success)' : 'var(--bs-info)',
                    opacity: 1,
                    transition: 'width 0.15s linear',
                    height: '100%',
                    boxShadow: '0 0 6px rgba(0, 0, 0, 0.4)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                />
              </div>
            </div>
            <span className="visually-hidden">
              {Math.round(delayRemaining / 1000)} {t('seconds')}
            </span>
          </>
        )}

        <BootstrapToast.Body className="position-relative">
          <div className="d-flex align-items-top">
            {toast.error && (
              <i
                className="bi bi-exclamation-triangle-fill"
                style={{ fontSize: '1.2em', width: '1.2em', height: '1.2em', color: 'var(--bs-danger)', flexShrink: 0 }}
              ></i>
            )}
            {!toast.error && toast.classname === 'success' && (
              <i
                className="bi bi-check-circle-fill"
                style={{ fontSize: '1.2em', width: '1.2em', height: '1.2em', color: 'var(--bs-success)', flexShrink: 0 }}
              ></i>
            )}
            {!toast.error && toast.classname !== 'success' && (
              <i
                className="bi bi-info-circle-fill"
                style={{ fontSize: '1.2em', width: '1.2em', height: '1.2em', color: 'var(--bs-info)', flexShrink: 0 }}
              ></i>
            )}
            <div className="flex-grow-1">
              <p className="ms-2 mb-0 text-break">{toast.content}</p>

              {toast.error && (
                <details className="ms-2">
                  <summary className="text-muted small">{t('details')}</summary>
                  <div className="mt-2 ms-n4 me-n2 small">
                    {isDetailedError(toast.error) && (
                      <dl className="row mb-0">
                        <dt className="col-sm-3 fw-normal text-end">URL</dt>
                        <dd className="col-sm-9">{typeof toast.error === 'object' && toast.error?.url}</dd>
                        <dt className="col-sm-3 fw-normal text-end">{t('status')}</dt>
                        <dd className="col-sm-9">
                          {typeof toast.error === 'object' && toast.error?.status}{' '}
                          <em>{typeof toast.error === 'object' && toast.error?.statusText}</em>
                        </dd>
                        <dt className="col-sm-3 fw-normal text-end">{t('error')}</dt>
                        <dd className="col-sm-9">{getErrorText(toast.error)}</dd>
                      </dl>
                    )}
                    <div className="row">
                      <div className="col offset-sm-3">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => copyError(toast.error)}>
                          {!copied ? (
                            <>
                              <i className="bi bi-clipboard"></i>&nbsp;
                            </>
                          ) : (
                            <>
                              <i className="bi bi-clipboard-check"></i>&nbsp;
                            </>
                          )}
                          {t('copy_raw_error')}
                        </button>
                      </div>
                    </div>
                  </div>
                </details>
              )}

              {toast.action && toast.actionName && (
                <p className="mb-0 mt-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleAction}>
                    {toast.actionName}
                  </button>
                </p>
              )}
            </div>

            <button type="button" className="btn-close ms-auto flex-shrink-0" aria-label={t('close')} onClick={handleClose} />
          </div>
        </BootstrapToast.Body>
      </BootstrapToast>
    </div>
  )
}
